import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import { API } from '../config';
import '../styles/theme.css';

export default function SalaryInsights() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const tier = localStorage.getItem('user_tier') || 'free';
  const isPremium = tier === 'premium';

  const fetchInsights = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const url = `${API}/insights/salary${search ? `?job_title=${encodeURIComponent(search)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed');
      setData(json);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Salary Insights</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>Salary Insights <span className="badge badge-purple" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>Premium</span></h1>
            <p>Explore salary ranges from real job postings on JobLinked AI</p>
          </div>

          {!isPremium ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <i className="fas fa-lock" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem', display: 'block' }}></i>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Premium Feature</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Salary insights are available on the Premium plan (R599/month).</p>
              <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
                <i className="fas fa-crown"></i> Upgrade to Premium
              </button>
            </div>
          ) : (
            <>
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <input
                    className="form-control"
                    placeholder="Search by job title (e.g. Data Scientist, Software Engineer)..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchInsights()}
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-primary" onClick={fetchInsights} disabled={loading}>
                    <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-search'}`}></i> Search
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i> {error}</div>
              )}

              {data && (
                <>
                  <div className="stats-row">
                    <div className="stat-card">
                      <div className="stat-icon-wrap blue"><i className="fas fa-briefcase"></i></div>
                      <div className="stat-info">
                        <div className="stat-value">{data.total_jobs_analyzed}</div>
                        <div className="stat-label">Jobs Analyzed</div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon-wrap green"><i className="fas fa-arrow-down"></i></div>
                      <div className="stat-info">
                        <div className="stat-value">R{data.market_min?.toLocaleString()}</div>
                        <div className="stat-label">Market Min</div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon-wrap orange"><i className="fas fa-chart-line"></i></div>
                      <div className="stat-info">
                        <div className="stat-value">R{data.market_avg?.toLocaleString()}</div>
                        <div className="stat-label">Market Average</div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon-wrap purple"><i className="fas fa-arrow-up"></i></div>
                      <div className="stat-info">
                        <div className="stat-value">R{data.market_max?.toLocaleString()}</div>
                        <div className="stat-label">Market Max</div>
                      </div>
                    </div>
                  </div>

                  <div className="card" style={{ marginTop: '1.5rem' }}>
                    <div className="card-header">
                      <span className="card-title">Salary by Experience Level — "{data.search}"</span>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Experience Level</th>
                            <th>Avg Min</th>
                            <th>Avg Max</th>
                            <th>Range</th>
                            <th>Sample Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.by_experience_level.map(row => (
                            <tr key={row.experience_level}>
                              <td><span className="badge badge-blue">{row.experience_level}</span></td>
                              <td>R{row.avg_min?.toLocaleString()}</td>
                              <td>R{row.avg_max?.toLocaleString()}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{
                                      height: '100%', borderRadius: 4,
                                      background: 'linear-gradient(90deg, #5d1a70, #640ff7)',
                                      width: `${Math.min((row.avg_max / data.market_max) * 100, 100)}%`
                                    }} />
                                  </div>
                                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    R{row.overall_min?.toLocaleString()} – R{row.overall_max?.toLocaleString()}
                                  </span>
                                </div>
                              </td>
                              <td style={{ color: 'var(--text-muted)' }}>{row.sample_size} job{row.sample_size !== 1 ? 's' : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {!data && !loading && !error && (
                <div className="empty-state">
                  <i className="fas fa-chart-bar"></i>
                  <h3>Search for salary data</h3>
                  <p>Enter a job title to see salary ranges from real postings</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
